/*!
 * Hazemor — "haze" layer. A lightweight WebGL steam field (domain-warped fbm) that drifts upward
 * over hero imagery. Renders at reduced resolution, pauses off-screen / in background tabs,
 * draws a single still frame under prefers-reduced-motion, and falls back to CSS without WebGL.
 */
(function () {
  'use strict';

  var VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  var FRAG = [
    'precision mediump float;',
    'uniform vec2 r;uniform float t;uniform vec2 m;uniform float k;uniform vec3 c1;uniform vec3 c2;',
    'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123);}',
    'float n(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.-2.*f);',
    'return mix(mix(h(i),h(i+vec2(1.,0.)),u.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.,1.)),u.x),u.y);}',
    'float fbm(vec2 p){float v=0.,a=.5;mat2 R=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<5;i++){v+=a*n(p);p=R*p;a*=.5;}return v;}',
    'void main(){',
    '  vec2 uv=gl_FragCoord.xy/r;',
    '  vec2 p=uv*vec2(r.x/r.y,1.)*1.8;',
    '  float s=t*.045;',
    '  p.y-=s*1.6;',
    '  p+= (m-.5)*vec2(.35,.22);',
    '  vec2 q=vec2(fbm(p+vec2(0.,s)),fbm(p+vec2(5.2,1.3)-s));',
    '  vec2 w=vec2(fbm(p+2.6*q+vec2(1.7,9.2)+.4*s),fbm(p+2.6*q+vec2(8.3,2.8)-.3*s));',
    '  float f=fbm(p+2.2*w);',
    '  float rise=smoothstep(1.15,-.05,uv.y);',
    '  float a=smoothstep(.42,1.02,f)*(.35+.65*rise)*k;',
    '  vec3 col=mix(c2,c1,uv.y+(.5-f)*.4);',
    '  gl_FragColor=vec4(col*a,a);',
    '}'
  ].join('\n');

  function hexToRgb(hex, fallback) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
    if (!m) return fallback;
    return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
  }

  function Haze(host) {
    this.host = host;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hz-haze__canvas';
    this.canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(this.canvas);
    var opts = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' };
    var gl = this.canvas.getContext('webgl', opts) || this.canvas.getContext('experimental-webgl', opts);
    if (!gl) {
      host.classList.add('is-fallback');
      return;
    }
    this.gl = gl;
    var prog = this.program(VERT, FRAG);
    if (!prog) {
      host.classList.add('is-fallback');
      return;
    }
    gl.useProgram(prog);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.u = {
      r: gl.getUniformLocation(prog, 'r'),
      t: gl.getUniformLocation(prog, 't'),
      m: gl.getUniformLocation(prog, 'm'),
      k: gl.getUniformLocation(prog, 'k'),
      c1: gl.getUniformLocation(prog, 'c1'),
      c2: gl.getUniformLocation(prog, 'c2')
    };
    var c1 = hexToRgb(host.getAttribute('data-color-top'), [1, 0.96, 0.9]);
    var c2 = hexToRgb(host.getAttribute('data-color-bottom'), [1, 0.86, 0.68]);
    gl.uniform3f(this.u.c1, c1[0], c1[1], c1[2]);
    gl.uniform3f(this.u.c2, c2[0], c2[1], c2[2]);
    this.k = parseFloat(host.getAttribute('data-intensity') || '0.9');
    this.mouse = [0.5, 0.5];
    this.target = [0.5, 0.5];
    this.visible = false;
    this.start = performance.now() - Math.random() * 20000;
    this.resize();
    this.bind();
    host.classList.add('is-ready');
  }

  Haze.prototype.program = function (vs, fs) {
    var gl = this.gl;
    var compile = function (type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
      return s;
    };
    var v = compile(gl.VERTEX_SHADER, vs);
    var f = compile(gl.FRAGMENT_SHADER, fs);
    if (!v || !f) return null;
    var p = gl.createProgram();
    gl.attachShader(p, v);
    gl.attachShader(p, f);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return null;
    return p;
  };

  Haze.prototype.resize = function () {
    var rect = this.host.getBoundingClientRect();
    var scale = Math.min(window.devicePixelRatio || 1, 1.5) * (window.innerWidth < 750 ? 0.42 : 0.5);
    var wpx = Math.max(2, Math.round(rect.width * scale));
    var hpx = Math.max(2, Math.round(rect.height * scale));
    if (this.canvas.width !== wpx || this.canvas.height !== hpx) {
      this.canvas.width = wpx;
      this.canvas.height = hpx;
      this.gl.viewport(0, 0, wpx, hpx);
      this.gl.uniform2f(this.u.r, wpx, hpx);
    }
  };

  Haze.prototype.draw = function (now) {
    var gl = this.gl;
    this.mouse[0] += (this.target[0] - this.mouse[0]) * 0.03;
    this.mouse[1] += (this.target[1] - this.mouse[1]) * 0.03;
    gl.uniform1f(this.u.t, (now - this.start) / 1000);
    gl.uniform2f(this.u.m, this.mouse[0], this.mouse[1]);
    gl.uniform1f(this.u.k, this.k);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  Haze.prototype.loop = function () {
    var self = this;
    if (this.raf) return;
    var last = 0;
    var tick = function (now) {
      self.raf = 0;
      if (!self.visible || document.hidden) return;
      // ~40fps is plenty for slow steam and kinder on batteries
      if (now - last > 24) {
        last = now;
        self.draw(now);
      }
      self.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  };

  Haze.prototype.bind = function () {
    var self = this;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.visible = e.isIntersecting;
        if (self.visible && !reduced.matches) self.loop();
      });
    });
    io.observe(this.host);
    if (reduced.matches) {
      this.draw(performance.now());
    }
    var ro = 'ResizeObserver' in window ? new ResizeObserver(function () {
      self.resize();
      if (reduced.matches) self.draw(performance.now());
    }) : null;
    if (ro) ro.observe(this.host);
    else window.addEventListener('resize', function () { self.resize(); });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && self.visible && !reduced.matches) self.loop();
    });
    var area = this.host.closest('[data-hz-haze-area]') || this.host.parentElement;
    if (area && window.matchMedia('(pointer: fine)').matches) {
      area.addEventListener('pointermove', function (e) {
        var r = area.getBoundingClientRect();
        self.target[0] = (e.clientX - r.left) / r.width;
        self.target[1] = 1 - (e.clientY - r.top) / r.height;
      }, { passive: true });
    }
  };

  function boot(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-hz-haze]'), function (el) {
      if (el.dataset.hzHazeDone) return;
      el.dataset.hzHazeDone = '1';
      try {
        new Haze(el);
      } catch (err) {
        el.classList.add('is-fallback');
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
